import { SmrtCollection } from '@happyvertical/smrt-core';
import { ChatReaction } from '../models/ChatReaction.js';

export class ChatReactionCollection extends SmrtCollection<ChatReaction> {
  static readonly _itemClass = ChatReaction;

  async getByMessage(messageId: string): Promise<ChatReaction[]> {
    return this.list({ where: { messageId } });
  }

  /** Get reaction counts grouped by emoji for a message */
  async getReactionCounts(
    messageId: string,
  ): Promise<Map<string, { count: number; profileIds: string[] }>> {
    const reactions = await this.list({ where: { messageId } });
    const counts = new Map<string, { count: number; profileIds: string[] }>();

    for (const reaction of reactions) {
      const existing = counts.get(reaction.emoji);
      if (existing) {
        existing.count++;
        existing.profileIds.push(reaction.profileId);
      } else {
        counts.set(reaction.emoji, {
          count: 1,
          profileIds: [reaction.profileId],
        });
      }
    }

    return counts;
  }

  /**
   * Toggle a reaction: add if not present, remove if already reacted.
   *
   * `tenantId` is bound into the existence lookup (S5 #1392) so the toggle can
   * never match or delete a reaction row belonging to another tenant. Prefer the
   * membership-checked {@link ChatService.addReaction}/{@link ChatService.removeReaction}
   * for request-driven flows; this low-level helper performs no membership check.
   */
  async toggle(
    messageId: string,
    profileId: string,
    emoji: string,
    tenantId: string,
  ): Promise<{ added: boolean }> {
    const existing = await this.list({
      where: { messageId, profileId, emoji, tenantId },
    });

    if (existing.length > 0) {
      await existing[0].delete();
      return { added: false };
    }

    await this.create({
      tenantId,
      messageId,
      profileId,
      emoji,
    });
    return { added: true };
  }
}
